import { useMemo } from "react"
import type { QueryKey } from "@tanstack/react-query"
import { useQueryClient } from "@tanstack/react-query"
import type { Event, EventCreate, EventUpdate, GetEvents200 } from "openapi/api/model"
import type { GetEventsParams } from "openapi/api/model/getEventsParams"
import {
  useDeleteEventsId,
  useGetEvents,
  useGetEventsBusySlots,
  usePostEvents,
  usePutEventsId,
} from "openapi/api/endpoints"
import { parseBusySlotDate } from "@/lib/calendar-busy-slots"

type BookingMode = "create" | "edit"

type UseCalendarBookingOptions = {
  selectedDate?: Date | null
  startTime?: string
  endTime?: string
  mode?: BookingMode
  currentProviderEventId?: string
}

const EVENTS_QUERY_KEY = "/events"

const matchesQueryFilter = (event: Event, params?: GetEventsParams) => {
  if (!params) return true
  if (params.job_id && event.job_id !== params.job_id) return false
  if (params.candidate_id && event.candidate_id !== params.candidate_id) return false
  return true
}

const extractEventsParams = (queryKey: QueryKey): GetEventsParams | undefined => {
  if (!Array.isArray(queryKey)) return undefined
  if (queryKey[0] !== EVENTS_QUERY_KEY) return undefined
  const maybeParams = queryKey[1]
  if (!maybeParams || typeof maybeParams !== "object") return undefined
  return maybeParams as GetEventsParams
}

const applyEventUpdate = (previous: Event, patch: EventUpdate): Event => ({
  ...previous,
  ...patch,
  candidate_id: patch.candidate_id === null ? undefined : (patch.candidate_id ?? previous.candidate_id),
  updated_at: new Date().toISOString(),
})

export const useCalendarBooking = ({
  selectedDate,
  startTime,
  endTime,
  mode = "create",
  currentProviderEventId,
}: UseCalendarBookingOptions = {}) => {
  const queryClient = useQueryClient()
  const resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const { data: events, isLoading } = useGetEvents()
  const { mutateAsync: postEvent } = usePostEvents()
  const { mutateAsync: putEvent } = usePutEventsId()
  const { mutateAsync: removeEvent } = useDeleteEventsId()

  const busyRange = useMemo(() => {
    if (!selectedDate) return undefined

    const start = new Date(selectedDate)
    start.setHours(0, 0, 0, 0)

    const end = new Date(selectedDate)
    end.setHours(23, 59, 59, 999)

    return {
      range_start: start.toISOString(),
      range_end: end.toISOString(),
      time_zone: resolvedTimeZone,
    }
  }, [selectedDate, resolvedTimeZone])

  const { data: busySlotsData, isPending: isBusySlotsPending } = useGetEventsBusySlots(
    busyRange,
    { query: { enabled: !!busyRange } },
  )

  const selectedInterval = useMemo(() => {
    if (!selectedDate || !startTime || !endTime) return null

    const [startHour, startMinute] = startTime.split(":").map(Number)
    const [endHour, endMinute] = endTime.split(":").map(Number)
    if (
      !Number.isFinite(startHour) ||
      !Number.isFinite(startMinute) ||
      !Number.isFinite(endHour) ||
      !Number.isFinite(endMinute)
    ) {
      return null
    }

    const start = new Date(selectedDate)
    start.setHours(startHour, startMinute, 0, 0)
    const end = new Date(selectedDate)
    end.setHours(endHour, endMinute, 0, 0)

    return { start, end }
  }, [selectedDate, startTime, endTime])

  const overlappingBusySlots = useMemo(() => {
    if (!selectedInterval || !busySlotsData?.slots?.length) return []

    return busySlotsData.slots.filter((slot) => {
      if (
        mode === "edit" &&
        currentProviderEventId &&
        slot?.eventId &&
        slot.eventId === currentProviderEventId
      ) {
        return false
      }

      if (!slot?.start || !slot?.end) return false
      const busyStart = parseBusySlotDate(slot.start)
      const busyEnd = parseBusySlotDate(slot.end)
      if (!busyStart || !busyEnd) return false

      return selectedInterval.start < busyEnd && selectedInterval.end > busyStart
    })
  }, [selectedInterval, busySlotsData?.slots, mode, currentProviderEventId])

  const invalidateEvents = async () => {
    await queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
  }

  const getEventsSnapshots = () => {
    return queryClient.getQueriesData<GetEvents200>({ queryKey: [EVENTS_QUERY_KEY] })
  }

  const restoreEventsSnapshots = (snapshots: Array<[QueryKey, GetEvents200 | undefined]>) => {
    snapshots.forEach(([queryKey, data]) => {
      queryClient.setQueryData(queryKey, data)
    })
  }

  const createEvent = async (data: EventCreate) => {
    await queryClient.cancelQueries({ queryKey: [EVENTS_QUERY_KEY] })
    const snapshots = getEventsSnapshots()

    const optimisticId = `optimistic-${Date.now()}`
    const optimisticEvent: Event = {
      id: optimisticId,
      title: data.title,
      event_type: data.event_type,
      start_at: data.start_at,
      end_at: data.end_at,
      job_id: data.job_id ?? null,
      candidate_id: data.candidate_id ?? undefined,
      location: data.location ?? null,
      notes: data.notes ?? null,
      invitee_emails: data.invitee_emails ?? [],
      time_zone: data.time_zone,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    snapshots.forEach(([queryKey, previous]) => {
      if (!previous) return
      const params = extractEventsParams(queryKey)
      if (!matchesQueryFilter(optimisticEvent, params)) return

      queryClient.setQueryData<GetEvents200>(queryKey, {
        ...previous,
        data: [optimisticEvent, ...(previous.data ?? [])],
      })
    })

    try {
      const response = await postEvent({ data })
      return response
    } catch (error) {
      restoreEventsSnapshots(snapshots)
      throw error
    } finally {
      await invalidateEvents()
    }
  }

  const updateEvent = async (id: string, data: EventUpdate) => {
    await queryClient.cancelQueries({ queryKey: [EVENTS_QUERY_KEY] })
    const snapshots = getEventsSnapshots()

    snapshots.forEach(([queryKey, previous]) => {
      if (!previous?.data) return

      const nextData = previous.data
        .map((event) => {
          if (event.id !== id) return event
          return applyEventUpdate(event, data)
        })
        .filter((event) => matchesQueryFilter(event, extractEventsParams(queryKey)))

      queryClient.setQueryData<GetEvents200>(queryKey, {
        ...previous,
        data: nextData,
      })
    })

    try {
      const response = await putEvent({ id, data })
      return response
    } catch (error) {
      restoreEventsSnapshots(snapshots)
      throw error
    } finally {
      await invalidateEvents()
    }
  }

  const deleteEvent = async (id: string) => {
    await queryClient.cancelQueries({ queryKey: [EVENTS_QUERY_KEY] })
    const snapshots = getEventsSnapshots()

    snapshots.forEach(([queryKey, previous]) => {
      if (!previous?.data) return

      queryClient.setQueryData<GetEvents200>(queryKey, {
        ...previous,
        data: previous.data.filter((event) => event.id !== id),
      })
    })

    try {
      const response = await removeEvent({ id })
      return response
    } catch (error) {
      restoreEventsSnapshots(snapshots)
      throw error
    } finally {
      await invalidateEvents()
    }
  }

  return {
    events,
    isLoading,
    isBusySlotsPending,
    overlappingBusySlots,
    createEvent,
    updateEvent,
    deleteEvent,
  }
}
